// Angular imports
import { Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, GuardsCheckStart, GuardsCheckEnd } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';

// devextreme imports
import dxScrollable from 'devextreme/ui/scroll_view/ui.scrollable';
import dxScrollView from 'devextreme/ui/scroll_view';

// 3rd party imports
import themes from 'devextreme/ui/themes';

// RxJS imports
import { filter } from 'rxjs/operators';

import {
    CCMessageBoxIcon,
    CCSessionManagerService,
    CCTokenRenewalService,
    MessageBoxComponent,
    CCLocalStorageService,
    CCLocalStorageCategory,
    Utils
} from 'cc-common';

// Service imports
import { ScreenService } from './_services/shared-services/screen.service';
import { UserBackgroundService } from './_services/shared-services/user-background.service';
import { AppInfoService } from './_services/app-info.service';
import { AuthService } from './_services/auth.service';
import { ServerErrorService } from './_services/shared-services/server-error.service';
import { RoutingHelperService } from './_services/shared-services/routing-helper.service';
import { UserService } from './_services/data-services/user.service';
import { ContextManagerService } from './_services/data-services/context-manager.service';
import { CommunityService } from './_services/data-services/community.service';

// Environment imports
import { environment } from '../environments/environment';

// Constant imports
import { StorageKeyConstants } from './_constants/storage-key-constants';
import { themeColors } from './_constants/theme-colors';
import { Constants } from './_constants/constants';

// Model imports
import { MessageBoxInfo } from './_models/message-box-info';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;
    appTitle: string;
    paramsPopup: boolean;
    jwtHelper = new JwtHelperService();
    isLoggedInSuccess = false;
    allowAnonymous: boolean;
    routeComplete = false;
    loadingVisible = false;
    isAuthenticated = false;

    @HostBinding('class') get getClass() {
        return Object.keys(this.screenService.sizes)
            .filter((cl) => this.screenService.sizes[cl])
            .join(' ');
    }

    constructor(
        public readonly appInfo: AppInfoService,
        private readonly title: Title,
        private readonly authService: AuthService,
        private readonly screenService: ScreenService,
        private readonly userBackgroundService: UserBackgroundService,
        private readonly router: Router,
        private readonly errorService: ServerErrorService,
        private readonly routingHelperService: RoutingHelperService,
        private readonly userService: UserService,
        private readonly sessionManager: CCSessionManagerService,
        private readonly tokenManager: CCTokenRenewalService,
        private readonly contextManager: ContextManagerService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly communityService: CommunityService
    ) {
        this.appTitle = this.appInfo.title;
        this.sessionManager.logoutHandler = authService.logOut.bind(authService);
        router.events.subscribe((e) => {
            if (e instanceof GuardsCheckStart) {
                this.setPageTitle();
                this.routingHelperService.isOutletOnly().subscribe((paramsPopup) => {
                    this.paramsPopup = paramsPopup;
                    this.allowAnonymous = this.routingHelperService.isAllowAnonymousAcess();
                    this.routingHelperService.resetRouteParameters();
                });
                if (
                    this.authService.isTokenExpired &&
                    e.urlAfterRedirects.indexOf('/user/') === -1 &&
                    e.urlAfterRedirects.indexOf('cirahelp') === -1
                ) {
                    this.errorService.updateSubjectErrorMessage(
                        'Application session is expired! Please login to continue.'
                    );
                }
            } else if (e instanceof GuardsCheckEnd) {
                this.routeComplete = true;
                /* Below commented code is removed as it no longer needed after Board Member release. Keeping the code for reference */
                // e.urlAfterRedirects.indexOf('cirahelp') > 0 || this.userService.isBoardMember();
            } else if (e instanceof NavigationEnd) {
                this.isAuthenticated = this.authService.isLoggedIn || this.allowAnonymous;
            }
        });
    }

    ngOnInit() {
        // User activity logging
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.logUserActivity(event);
        });

        // preference changes
        this.userService.getUserPreferences().subscribe((preferences) => {
            if (preferences) {
                this.setApplicationTheme();
                this.setScrollbarOptions();
            }
        });

        this.authService.getSubjectLoginSuccess().subscribe((flag) => {
            if (flag) {
                this.setAppManagers();
                this.setApplicationTheme();
                this.setScrollbarOptions();
                this.loadingVisible = true;
                this.userService.loadUserSettings().subscribe(() => {
                    this.communityService.loadCommunities().subscribe(() => (this.loadingVisible = false));
                });
            }
        });

        // error message
        this.errorService.getSubjectErrorMessage().subscribe((error) => {
            this.messageBox.showWithFixedWidthAndIcon(CCMessageBoxIcon.error, 'Error', error, 320);
        });

        // other message
        this.errorService.getSubjectMessage().subscribe((info: MessageBoxInfo) => {
            this.messageBox.showWithFixedWidthAndIcon(info.icon, info.title, info.message, 320);
        });

        // Setting Dynamic Page Title - BEGIN
        this.setPageTitle();
        this.contextManager.getContext().subscribe(() => {
            this.setPageTitle();
        });
        // Setting Dynamic Page Title - END

        // Default loads on application init
        this.setAppManagers();
        this.setApplicationTheme();
        this.setScrollbarOptions();
        this.isAuthenticated = this.authService.isLoggedIn || this.allowAnonymous;
        if (this.isAuthenticated) {
            this.loadingVisible = true;
            this.communityService.loadCommunities().subscribe(() => (this.loadingVisible = false));
        }

        const choice = this.localStorageService.getItem(
            StorageKeyConstants.userOpenLinksKey,
            CCLocalStorageCategory.PREF
        );
        if (!choice) {
            this.localStorageService.setItem(
                StorageKeyConstants.userOpenLinksKey,
                environment.defaultOpenLinks,
                CCLocalStorageCategory.PREF
            );
        }
        this.localStorageService.setItem(StorageKeyConstants.userDownloadsKey, 'download', CCLocalStorageCategory.PREF);
    }

    onActivate(event): void {
        window.scroll(0, 0);
    }

    logUserActivity(event: NavigationEnd): void {
        const urlInfoList = event.url.split('?');
        const url = Utils.notNullOrEmpty(urlInfoList) ? urlInfoList[0] : event.url;
        this.userBackgroundService.logActivity(url, 'Navigation', new Date(), 'success');
        if (environment.enableUserSession) {
            this.sessionManager.reset();
        }
    }

    isPopup() {
        return this.paramsPopup;
    }

    isLoginUrl() {
        return window.location.href.indexOf('/user/login') !== -1;
    }

    private setApplicationTheme() {
        // Get user preferred theme color from local storage. If not specified use default theme color for the application
        let themeColor = this.localStorageService.getItem(
            StorageKeyConstants.userThemeKey,
            CCLocalStorageCategory.PREF
        );
        if (!themeColor) {
            this.localStorageService.setItem(
                StorageKeyConstants.userThemeKey,
                environment.defaultThemeColor,
                CCLocalStorageCategory.PREF
            );
            themeColor = environment.defaultThemeColor;
        }

        const theme = themeColors.filter((t) => t.name === themeColor);
        if (!!theme && theme.length > 0) {
            const smallDevice = false;
            // NOTE: We need to take a call of changing theme style dynamically for small devices using the same color scheme.
            // Until then we need to keep the commented code below.
            // if (window.screen.width <= environment.smallDeviceMaxWidth) {
            //   smallDevice = true;
            // }
            this.setCustomStyleSheet(theme[0].customStylesheetName);
            if (smallDevice) {
                themes.current(theme[0].materialStyleName);
            } else {
                themes.current(theme[0].compactStyleName);
            }
        }
    }

    private setCustomStyleSheet(styleName: string) {
        const themeLinks: HTMLCollectionOf<HTMLLinkElement> = document.getElementsByTagName('link');
        const customThemes: HTMLLinkElement[] = [].slice.call(themeLinks);
        customThemes.forEach((t: HTMLLinkElement) => {
            // Remove all existing custom style sheet links
            const dataRel: Attr = t.attributes.getNamedItem('data-rel');
            if (dataRel && dataRel.value === 'cc-theme' && t.rel === 'stylesheet') {
                t.remove();
            }

            // Add the new custom style sheet based on input style name
            if (t.rel === 'cc-theme') {
                const dataTheme: Attr = t.attributes.getNamedItem('data-theme');
                if (dataTheme.value === styleName) {
                    /* Create link document */
                    const link = document.createElement('link');
                    link.type = 'text/css';
                    link.rel = 'stylesheet';
                    link.setAttribute('data-theme', styleName);
                    link.setAttribute('data-rel', 'cc-theme');
                    link.href = t.href;
                    /* Append link to the tag name */
                    document.getElementsByTagName('head')[0].appendChild(link);
                }
            }
        });
    }

    private setAppManagers(): void {
        if (environment.enableUserSession) {
            if (this.authService.isLoggedIn) {
                this.sessionManager.activate();
                this.tokenManager.activate();
            } else {
                this.sessionManager.deactivate();
                this.tokenManager.deactivate();
            }
        }
    }

    private setPageTitle(): void {
        const context = this.contextManager.getSelectedContext();
        const tokens = [];
        if (!!context) {
            if (!!context.propertyAddress) {
                tokens.push(context.propertyAddress);
            }
            if (!!context.communityName) {
                tokens.push(context.communityName);
            }
        }
        const title = tokens.length > 0 ? tokens.join(' - ') : environment.defaultWindowTitle;
        window.document.title = title;
        this.title.setTitle(title);
    }

    private setScrollbarOptions(): void {
        // tslint:disable-next-line:deprecation
        dxScrollable.defaultOptions({
            options: this.getScrollbarOptions()
        });
        dxScrollView.defaultOptions({
            options: this.getScrollbarOptions()
        });
    }

    private getScrollbarOptions(): any {
        const scrollbarPreference =
            this.localStorageService.getItem(StorageKeyConstants.userScrollbarKey, CCLocalStorageCategory.PREF) ||
            Constants.defaultScrollbarView;
        const scrollbarType =
            this.localStorageService.getItem(StorageKeyConstants.userScrollTypeKey, CCLocalStorageCategory.PREF) ||
            Constants.defaultScrollbarType;

        return {
            useNative: scrollbarType !== Constants.defaultScrollbarType,
            showScrollbar: scrollbarPreference
        };
    }
}
